import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, PlayCircleIcon, ZoomInIcon } from 'lucide-react';
interface Feature {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  detailedDescription?: string;
  image?: string;
}
interface CapabilityModalProps {
  feature: Feature | null;
  onClose: () => void;
}
export function CapabilityModal({
  feature,
  onClose
}: CapabilityModalProps) {
  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  useEffect(() => {
    if (feature) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setIsImageEnlarged(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [feature]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isImageEnlarged) {
          setIsImageEnlarged(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, isImageEnlarged]);
  return <AnimatePresence data-id="element-57">
      {feature && <>
          {/* Backdrop */}
          <motion.div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} data-id="element-58" />


          {/* Modal */}
          <motion.div className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6 pointer-events-none" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-59">

            <motion.div className="relative w-full max-w-4xl max-h-[85vh] overflow-y-auto pointer-events-auto rounded-2xl border border-white/10 bg-[#0D0B2E]/95 backdrop-blur-xl shadow-2xl" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-60">

              {/* Close button */}
              <button onClick={onClose} className="absolute top-5 right-5 z-10 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" aria-label="Close modal" data-id="element-61">

                <XIcon className="w-5 h-5 text-white/60" data-id="element-62" />
              </button>

              <div className={`grid grid-cols-1 ${feature.image ? 'lg:grid-cols-5' : ''}`} data-id="element-63">

                {/* Content Side */}
                <div className={`${feature.image ? 'lg:col-span-3' : ''} p-8 sm:p-10`} data-id="element-64">

                  {/* Icon & Label */}
                  <div className="flex items-center gap-4 mb-6" data-id="element-65">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-electric/25 to-violet-electric/5 flex items-center justify-center flex-shrink-0" data-id="element-66">
                      <feature.icon className="w-6 h-6 text-violet-electric" data-id="element-67" />
                    </div>
                    <div data-id="element-68">
                      <span className="text-violet-electric text-[10px] font-bold uppercase tracking-widest block mb-0.5" data-id="element-69">
                        Capability
                      </span>
                      <h2 className="text-2xl font-bold text-white leading-tight" data-id="element-70">
                        {feature.title}
                      </h2>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-full h-px bg-gradient-to-r from-violet-electric/20 via-white/[0.06] to-transparent mb-6" data-id="element-71" />

                  {/* Description */}
                  <p className="text-white/70 text-base leading-relaxed mb-6" data-id="element-72">
                    {feature.description}
                  </p>

                  {/* Detailed Description */}
                  {feature.detailedDescription && <div className="space-y-4 mb-8" data-id="element-73">
                      {feature.detailedDescription.split('\n\n').map((paragraph, i) => <p key={i} className="text-white/55 text-sm leading-relaxed" data-id="element-74">

                            {paragraph}
                          </p>)}
                    </div>}

                  {/* CTA */}
                  <div className="flex flex-wrap gap-3 mt-8" data-id="element-75">
                    <a href="/#/resources/demo-videos" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all" data-id="element-76">

                      See it in action
                      <ArrowRightIcon className="w-4 h-4" data-id="element-77" />
                    </a>
                    <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-6 py-2.5 rounded-full border border-white/20 text-white font-medium text-sm hover:border-white/40 transition-all" data-id="element-78">

                      Talk to an expert
                    </a>
                  </div>
                </div>

                {/* Media Side */}
                {feature.image && <div className="lg:col-span-2 p-6 sm:p-8 lg:p-0 lg:flex lg:items-center" data-id="element-79">
                    <button onClick={() => setIsImageEnlarged(true)} className="relative w-full lg:h-full lg:min-h-[400px] rounded-xl lg:rounded-none lg:rounded-r-2xl overflow-hidden border border-white/[0.06] lg:border-0 lg:border-l lg:border-white/[0.06] bg-white/[0.02] flex items-center justify-center p-4 group cursor-zoom-in" data-id="element-80">

                      <img src={feature.image} alt={feature.title} className="w-full h-full object-contain" data-id="element-81" />

                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center" data-id="element-82">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/10 backdrop-blur-sm rounded-full p-3" data-id="element-83">
                          <ZoomInIcon className="w-6 h-6 text-white" data-id="element-84" />
                        </div>
                      </div>
                    </button>
                  </div>}
              </div>
            </motion.div>
          </motion.div>

          {/* Enlarged Image Modal */}
          <AnimatePresence data-id="element-85">
            {isImageEnlarged && feature.image && <>
                <motion.div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setIsImageEnlarged(false)} data-id="element-86" />

                <motion.div className="fixed inset-0 z-[201] flex items-center justify-center p-8 pointer-events-none" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-87">

                  <motion.div className="relative max-w-7xl max-h-full pointer-events-auto" initial={{
              scale: 0.9
            }} animate={{
              scale: 1
            }} exit={{
              scale: 0.9
            }} transition={{
              duration: 0.2
            }} data-id="element-88">

                    <button onClick={() => setIsImageEnlarged(false)} className="absolute -top-12 right-0 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close enlarged view" data-id="element-89">

                      <XIcon className="w-6 h-6 text-white" data-id="element-90" />
                    </button>
                    <img src={feature.image} alt={feature.title} className="max-w-full max-h-[calc(100vh-8rem)] object-contain rounded-lg shadow-2xl cursor-zoom-out" onClick={() => setIsImageEnlarged(false)} data-id="element-91" />

                  </motion.div>
                </motion.div>
              </>}
          </AnimatePresence>
        </>}
    </AnimatePresence>;
}